import React, { useState, useEffect, useRef } from 'react';
import styled from "styled-components";

const Wrapper = styled.div<{ height: number }>`
  height: ${props => props.height}px;
  background-color: #F5F5F5;
`;


const StickyBanner = styled.div`
  box-sizing: border-box;
  position: fixed;
  width: 100%;
  left: 0px;
  top: 0px;
  background: linear-gradient(275.4deg, #074199 45.31%, #0B6DFF 59.75%);
  border-bottom: 4px solid #0558D4;
  box-shadow: 0px 10px 15px -3px rgba(0, 0, 0, 0.1), 0px 4px 6px -4px rgba(0, 0, 0, 0.1);
  border-radius: 0px;
  z-index: 1000;
`;

const StickyContent = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  padding: 12px 16px;

  @media (min-width: 900px) {
    padding: 10px 32px 8px 16px;
  }
`;

const StickyIcon = styled.div`
  display: none;
  @media (min-width: 900px) {
    display: block;
  }
`;

const StickyText = styled.div`
  font-size: 16px;
  color: white;
  margin-left: 10px;
`;

const StickyTextLine1 = styled.div`
  font-family: 'Oswald';
  font-style: normal;
  font-weight: 700;
  font-size: 10px;
  line-height: 14px;
  letter-spacing: 0.4px;
  color: #FFFFFF;

  @media (min-width: 769px) {
    font-size: 12px;
    line-height: 24px;
  }
`;

const StickyTextLine2 = styled.div`
  font-family: 'Oswald';
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 14px;
  color: #FFFFFF;

  @media (min-width: 900px) {
    font-size: 16px;
    line-height: 16px;
  }
`;

const StickyTimer = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 6px 12px;
  gap: 12px;
  margin-left: auto;

  background: rgba(255, 255, 255, 0.1);
  border: 2px solid rgba(255, 255, 255, 0.5);
  border-radius: 1000px;

  @media (min-width: 769px) {
    padding: 3px 16px 3px 12px
  }
`;

const StickyTimerIcon = styled.div`
  width: 12px;
  height: 12px;
  display: flex;
  svg {
    width: 100%;
    height: 100%;
  }
  @media (min-width: 769px) {
    width: 24px;
    height: 24px;
  }
`;

const StickyTimerText = styled.div``;

const StickyTimerTextLine1 = styled.div`
  font-family: 'Oswald';
  font-style: normal;
  font-weight: 500;
  font-size: 10px;
  line-height: 15px;
  letter-spacing: 0.7px;
  text-transform: uppercase;
  color: #FFFFFF;
  display: none;

  @media (min-width: 769px) {
    display: block;
  }
`;

const StickyTimerTextLine2 = styled.div`
  font-family: 'Oswald';
  font-style: normal;
  font-weight: 700;
  color: #FFFFFF;
  letter-spacing: 0px;
  line-height: 12px;

  & > span.digit {
    font-size: 12px;
    @media (min-width: 769px) {
      font-size: 14px;
    }
  }
  & > span.unit {
    font-size: 8px;
    @media (min-width: 769px) {
      font-size: 10px;
    }
  }

  @media (min-width: 769px) {
    line-height: 21px;
  }
`;

interface Project60StickyBannerProps {
  line1?: string;
  line2?: string;
  showTimer?: boolean;
}

export const Project60StickyBanner = ({
  line1 = "NEW YEARS SPECIAL",
  line2 = "Get 20% Off + 14 Days Free",
  showTimer = true
}: Project60StickyBannerProps) => {
  const [timeRemaining, setTimeRemaining] = useState<number[]>([0, 0, 0]);
  const [bannerHeight, setBannerHeight] = useState<number>(0);
  const bannerRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    // UTC time for Dec.31 11:59pm PST
    const endDate = new Date('2026-01-01T07:59:00Z');

    const updateTimer = () => {
      const now = new Date();
      const diff = endDate.getTime() - now.getTime();

      if (diff <= 0) {
        setTimeRemaining([0, 0, 0]);
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

      setTimeRemaining([days, hours, minutes]);
    };

    // Update immediately
    updateTimer();

    // Update every second
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const bannerElement = bannerRef.current;
    if (!bannerElement) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { height } = entry.contentRect;
        setBannerHeight(height);
      }
    });

    resizeObserver.observe(bannerElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <Wrapper height={bannerHeight}>
      <StickyBanner ref={bannerRef}>
        <StickyContent>
          <StickyIcon>
            <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M0 18C0 8.05888 8.05888 0 18 0C27.9411 0 36 8.05888 36 18C36 27.9411 27.9411 36 18 36C8.05888 36 0 27.9411 0 18Z" fill="white" fill-opacity="0.1"/>
              <path d="M24 15H12C11.5858 15 11.25 15.3358 11.25 15.75V17.25C11.25 17.6642 11.5858 18 12 18H24C24.4142 18 24.75 17.6642 24.75 17.25V15.75C24.75 15.3358 24.4142 15 24 15Z" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M18 15V24.75" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M23.25 18V23.25C23.25 23.6478 23.092 24.0294 22.8107 24.3107C22.5294 24.592 22.1478 24.75 21.75 24.75H14.25C13.8522 24.75 13.4706 24.592 13.1893 24.3107C12.908 24.0294 12.75 23.6478 12.75 23.25V18" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M14.625 15.0003C14.1277 15.0003 13.6508 14.8028 13.2992 14.4512C12.9475 14.0995 12.75 13.6226 12.75 13.1253C12.75 12.628 12.9475 12.1511 13.2992 11.7995C13.6508 11.4479 14.1277 11.2503 14.625 11.2503C15.3485 11.2377 16.0575 11.5888 16.6596 12.2577C17.2616 12.9266 17.7287 13.8824 18 15.0003C18.2713 13.8824 18.7384 12.9266 19.3404 12.2577C19.9425 11.5888 20.6515 11.2377 21.375 11.2503C21.8723 11.2503 22.3492 11.4479 22.7008 11.7995C23.0525 12.1511 23.25 12.628 23.25 13.1253C23.25 13.6226 23.0525 14.0995 22.7008 14.4512C22.3492 14.8028 21.8723 15.0003 21.375 15.0003" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </StickyIcon>
          <StickyText>
            <StickyTextLine1>{line1}</StickyTextLine1>
            <StickyTextLine2>{line2}</StickyTextLine2>
          </StickyText>
          {showTimer && <StickyTimer>
            <StickyTimerIcon>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g opacity="0.99604">
                <path d="M12 6V12L16 14" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M11.9961 22C17.5189 22 21.9961 17.5228 21.9961 12C21.9961 6.47715 17.5189 2 11.9961 2C6.47325 2 1.99609 6.47715 1.99609 12C1.99609 17.5228 6.47325 22 11.9961 22Z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </g>
              </svg>
            </StickyTimerIcon>
            <StickyTimerText>
              <StickyTimerTextLine1>access ends</StickyTimerTextLine1>
              <StickyTimerTextLine2>
                <span className="digit">{timeRemaining[0] || '0'}</span><span className="unit">d</span>{" "}
                <span className="digit">{timeRemaining[1] || '0'}</span><span className="unit">h</span>{" "}
                <span className="digit">{timeRemaining[2] || '0'}</span><span className="unit">m</span>
              </StickyTimerTextLine2>
            </StickyTimerText>
          </StickyTimer>}
        </StickyContent>
      </StickyBanner>
    </Wrapper>
  );    
};
